import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Image,
  Clock,
  Car,
  Phone,
  Globe,
  Navigation,
  DollarSign,
  Building2,
  Map
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminPlacesApi, adminCategoriesApi } from '../../services/adminApi';

const AdminPlacesPage = () => {
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Temel Bilgiler
    name: '',
    description: '',
    short_description: '',
    image: '',
    gallery: [],
    category_id: '',
    category_name: '',
    rating: 4.5,
    review_count: 0,
    featured: false,
    
    // Konum Bilgileri
    address: '',
    district: '',
    latitude: '',
    longitude: '',
    maps_url: '',
    distance: '0 km',
    
    // İletişim Bilgileri
    phone: '',
    website: '',
    email: '',
    
    // Ziyaret Bilgileri
    hours: '',
    best_time: '',
    duration: '',
    entrance_fee: 'free',
    fee_amount: '',
    
    // Ulaşım ve Özellikler
    transport_type: 'car',
    parking: false,
    wheelchair_accessible: false,
    pet_friendly: false,
    child_friendly: false,
    photo_spot: false,
    
    // Ek Bilgiler
    tips: '',
    warnings: '',
    nearby_places: [],
    tags: []
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [newTag, setNewTag] = useState('');

  // Giresun ilçeleri
  const districts = [
    'Merkez', 'Bulancak', 'Espiye', 'Görele', 'Tirebolu', 'Keşap', 
    'Eynesil', 'Piraziz', 'Dereli', 'Yağlıdere', 'Doğankent', 
    'Güce', 'Şebinkarahisar', 'Alucra', 'Çamoluk', 'Çanakçı'
  ];

  const entranceFeeOptions = [
    { value: 'free', label: 'Ücretsiz' },
    { value: 'paid', label: 'Ücretli' },
    { value: 'donation', label: 'Bağış' }
  ];

  const transportOptions = [
    { value: 'car', label: 'Araba' },
    { value: 'bus', label: 'Otobüs' },
    { value: 'walk', label: 'Yürüyüş' },
    { value: 'boat', label: 'Tekne' }
  ];

  const fetchPlaces = async () => {
    setLoading(true);
    try {
      const data = await adminPlacesApi.getAll({
        search,
        category: categoryFilter,
        page,
        limit: 20
      });
      setPlaces(data.places);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch places:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await adminCategoriesApi.getAll();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPlaces();
  }, [search, categoryFilter, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      // Clean up data before sending
      const dataToSend = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        review_count: parseInt(formData.review_count) || 0,
        rating: parseFloat(formData.rating) || 4.5
      };
      
      if (editingPlace) {
        await adminPlacesApi.update(editingPlace.id, dataToSend);
      } else {
        await adminPlacesApi.create(dataToSend);
      }
      fetchPlaces();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save place:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (place) => {
    setEditingPlace(place);
    setFormData({
      name: place.name || '',
      description: place.description || '',
      short_description: place.short_description || '',
      image: place.image || '',
      gallery: place.gallery || [],
      category_id: place.category_id || '',
      category_name: place.category_name || '',
      rating: place.rating || 4.5,
      review_count: place.review_count || 0,
      featured: place.featured || false,
      address: place.address || '',
      district: place.district || '',
      latitude: place.latitude || '',
      longitude: place.longitude || '',
      maps_url: place.maps_url || '',
      distance: place.distance || '0 km',
      phone: place.phone || '',
      website: place.website || '',
      email: place.email || '',
      hours: place.hours || '',
      best_time: place.best_time || '',
      duration: place.duration || '',
      entrance_fee: place.entrance_fee || 'free',
      fee_amount: place.fee_amount || '',
      transport_type: place.transport_type || 'car',
      parking: place.parking || false,
      wheelchair_accessible: place.wheelchair_accessible || false,
      pet_friendly: place.pet_friendly || false,
      child_friendly: place.child_friendly || false,
      photo_spot: place.photo_spot || false,
      tips: place.tips || '',
      warnings: place.warnings || '',
      nearby_places: place.nearby_places || [],
      tags: place.tags || []
    });
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu mekanı silmek istediğinizden emin misiniz?')) return;
    try {
      await adminPlacesApi.delete(id);
      fetchPlaces();
    } catch (error) {
      console.error('Failed to delete place:', error);
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await adminPlacesApi.toggleFeatured(id);
      fetchPlaces();
    } catch (error) {
      console.error('Failed to toggle featured:', error);
    }
  };

  const resetForm = () => {
    setEditingPlace(null);
    setActiveTab('basic');
    setFormData({
      name: '',
      description: '',
      short_description: '',
      image: '',
      gallery: [],
      category_id: '',
      category_name: '',
      rating: 4.5,
      review_count: 0,
      featured: false,
      address: '',
      district: '',
      latitude: '',
      longitude: '',
      maps_url: '',
      distance: '0 km',
      phone: '',
      website: '',
      email: '',
      hours: '',
      best_time: '',
      duration: '',
      entrance_fee: 'free',
      fee_amount: '',
      transport_type: 'car',
      parking: false,
      wheelchair_accessible: false,
      pet_friendly: false,
      child_friendly: false,
      photo_spot: false,
      tips: '',
      warnings: '',
      nearby_places: [],
      tags: []
    });
  };

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setFormData({
      ...formData,
      category_id: categoryId,
      category_name: category?.name || ''
    });
  };

  const addGalleryImage = () => {
    if (newGalleryUrl.trim()) {
      setFormData({
        ...formData,
        gallery: [...formData.gallery, newGalleryUrl.trim()]
      });
      setNewGalleryUrl('');
    }
  };

  const removeGalleryImage = (index) => {
    setFormData({
      ...formData,
      gallery: formData.gallery.filter((_, i) => i !== index)
    });
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const tabs = [
    { id: 'basic', label: 'Temel Bilgiler', icon: Building2 },
    { id: 'location', label: 'Konum', icon: MapPin },
    { id: 'contact', label: 'İletişim', icon: Phone },
    { id: 'visit', label: 'Ziyaret', icon: Clock },
    { id: 'features', label: 'Özellikler', icon: Star },
    { id: 'extra', label: 'Ek Bilgiler', icon: Globe }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Mekanlar</h1>
          <p className="text-slate-400 mt-1">Toplam {total} mekan</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni Mekan
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="Mekan ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        >
          <option value="all">Tüm Kategoriler</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : places.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          Mekan bulunamadı
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {places.map((place) => (
            <div key={place.id} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden group hover:border-slate-600 transition-all">
              <div className="relative h-40">
                <img src={place.image} alt={place.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {place.featured && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-yellow-500 rounded-full text-xs font-medium text-black flex items-center gap-1">
                    <Star className="w-3 h-3" /> Öne Çıkan
                  </div>
                )}
                {place.district && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-slate-900/70 rounded-full text-xs text-white">
                    {place.district}
                  </div>
                )}
                <div className="absolute bottom-2 left-2 flex items-center gap-2">
                  <span className="flex items-center gap-1 text-white text-sm">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    {place.rating}
                  </span>
                  {place.entrance_fee === 'free' && (
                    <span className="px-2 py-0.5 bg-green-500/80 rounded text-xs text-white">Ücretsiz</span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-medium truncate">{place.name}</h3>
                <p className="text-slate-400 text-sm truncate mt-1">{place.category_name}</p>
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => handleToggleFeatured(place.id)}
                    className={`p-2 rounded-lg transition-colors ${place.featured ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-700 text-slate-400 hover:text-white'}`}
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(place)}
                      className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(place.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-slate-400">Sayfa {page} / {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingPlace ? 'Mekan Düzenle' : 'Yeni Mekan'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-700 px-4 overflow-x-auto">
              <div className="flex gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-emerald-500 text-emerald-400'
                          : 'border-transparent text-slate-400 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
              
              {/* Temel Bilgiler Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Mekan Adı *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Örn: Giresun Kalesi"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Kısa Açıklama</label>
                    <Input
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      placeholder="Kartlarda görünecek kısa açıklama"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Detaylı Açıklama *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      placeholder="Mekan hakkında detaylı bilgi..."
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Ana Görsel URL *</label>
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      required
                      placeholder="https://..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    {formData.image && (
                      <img src={formData.image} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Galeri Görselleri</label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={newGalleryUrl}
                        onChange={(e) => setNewGalleryUrl(e.target.value)}
                        placeholder="Görsel URL ekle..."
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                      <Button type="button" onClick={addGalleryImage} className="bg-emerald-500 hover:bg-emerald-600">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.gallery.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.gallery.map((url, index) => (
                          <div key={index} className="relative group">
                            <img src={url} alt={`Gallery ${index}`} className="w-20 h-20 object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Kategori *</label>
                      <select
                        value={formData.category_id}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        required
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500"
                      >
                        <option value="">Seçin</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">İlçe</label>
                      <select
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500"
                      >
                        <option value="">Seçin</option>
                        {districts.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Puan</label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={formData.rating}
                        onChange={(e) => setFormData({ ...formData, rating: parseFloat(e.target.value) })}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Yorum Sayısı</label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.review_count}
                        onChange={(e) => setFormData({ ...formData, review_count: parseInt(e.target.value) || 0 })}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                    />
                    <label htmlFor="featured" className="text-slate-300">Öne çıkan mekan</label>
                  </div>
                </div>
              )}

              {/* Konum Tab */}
              {activeTab === 'location' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Adres</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      placeholder="Tam adres..."
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none focus:border-emerald-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Enlem (Latitude)</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        placeholder="40.9128"
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Boylam (Longitude)</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        placeholder="38.3895"
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Google Haritalar Linki
                      <span className="text-emerald-400 ml-2 text-xs">(Önerilen)</span>
                    </label>
                    <Input
                      value={formData.maps_url}
                      onChange={(e) => setFormData({ ...formData, maps_url: e.target.value })}
                      placeholder="https://maps.app.goo.gl/... veya https://goo.gl/maps/..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Google Haritalar'da konumu açın → Paylaş → Bağlantıyı kopyala
                    </p>
                  </div>
                  
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <p className="text-sm text-slate-400 flex items-center gap-2">
                      <Map className="w-4 h-4" />
                      💡 Harita linki varsa, "Yol Tarifi Al" butonu bu linki açacaktır
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Merkeze Uzaklık</label>
                    <Input
                      value={formData.distance}
                      onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                      placeholder="Örn: 15 km"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
              )}

              {/* İletişim Tab */}
              {activeTab === 'contact' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Telefon</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="0454 XXX XX XX"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Web Sitesi</label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">E-posta</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="info@example.com"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Ziyaret Tab */}
              {activeTab === 'visit' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Çalışma Saatleri</label>
                    <Input
                      value={formData.hours}
                      onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                      placeholder="Örn: 09:00 - 18:00 (Her gün)"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">En İyi Ziyaret Zamanı</label>
                    <Input
                      value={formData.best_time}
                      onChange={(e) => setFormData({ ...formData, best_time: e.target.value })}
                      placeholder="Örn: Mayıs-Eylül arası"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Tahmini Ziyaret Süresi</label>
                    <Input
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      placeholder="Örn: 2 saat"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Giriş Ücreti</label>
                      <select
                        value={formData.entrance_fee}
                        onChange={(e) => setFormData({ ...formData, entrance_fee: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500"
                      >
                        {entranceFeeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Ücret Miktarı</label>
                      <Input
                        value={formData.fee_amount}
                        onChange={(e) => setFormData({ ...formData, fee_amount: e.target.value })}
                        placeholder="Örn: 50 TL"
                        disabled={formData.entrance_fee === 'free'}
                        className="bg-slate-900 border-slate-600 text-white disabled:opacity-50"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Ulaşım Türü</label>
                    <select
                      value={formData.transport_type}
                      onChange={(e) => setFormData({ ...formData, transport_type: e.target.value })}
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-emerald-500"
                    >
                      {transportOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Özellikler Tab */}
              {activeTab === 'features' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-400 mb-4">Mekanın sahip olduğu özellikleri işaretleyin:</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.parking}
                        onChange={(e) => setFormData({ ...formData, parking: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <span className="text-slate-300">🅿️ Otopark Mevcut</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.wheelchair_accessible}
                        onChange={(e) => setFormData({ ...formData, wheelchair_accessible: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <span className="text-slate-300">♿ Engelli Erişimi</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.pet_friendly}
                        onChange={(e) => setFormData({ ...formData, pet_friendly: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <span className="text-slate-300">🐕 Evcil Hayvan Dostu</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.child_friendly}
                        onChange={(e) => setFormData({ ...formData, child_friendly: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <span className="text-slate-300">👶 Çocuk Dostu</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.photo_spot}
                        onChange={(e) => setFormData({ ...formData, photo_spot: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <span className="text-slate-300">📸 Fotoğraf Noktası</span>
                    </label>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Etiketler</label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Etiket ekle..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                      <Button type="button" onClick={addTag} className="bg-emerald-500 hover:bg-emerald-600">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm flex items-center gap-1">
                            {tag}
                            <button type="button" onClick={() => removeTag(tag)} className="hover:text-white">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ek Bilgiler Tab */}
              {activeTab === 'extra' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Ziyaret İpuçları
                      <span className="text-emerald-400 ml-2 text-xs">(Her satır ayrı ipucu olarak görünür)</span>
                    </label>
                    <textarea
                      value={formData.tips}
                      onChange={(e) => setFormData({ ...formData, tips: e.target.value })}
                      rows={5}
                      placeholder="Her satıra bir ipucu yazın:&#10;🕐 Sabah erken saatlerde ziyaret edin&#10;📸 Fotoğraf için en iyi saat gün batımı&#10;👟 Rahat ayakkabı giyin"
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none focus:border-emerald-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Bu ipuçları "İpuçları" sekmesinde ana içerik olarak görünecektir
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Uyarılar</label>
                    <textarea
                      value={formData.warnings}
                      onChange={(e) => setFormData({ ...formData, warnings: e.target.value })}
                      rows={3}
                      placeholder="Dikkat edilmesi gereken hususlar..."
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none focus:border-emerald-500"
                    />
                  </div>
                </div>
              )}
            </form>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-between items-center">
              <div className="text-sm text-slate-400">
                {activeTab !== 'basic' && (
                  <button
                    type="button"
                    onClick={() => {
                      const currentIndex = tabs.findIndex(t => t.id === activeTab);
                      if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1].id);
                    }}
                    className="text-emerald-400 hover:text-emerald-300"
                  >
                    ← Önceki
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  İptal
                </Button>
                {activeTab !== 'extra' ? (
                  <Button
                    type="button"
                    onClick={() => {
                      const currentIndex = tabs.findIndex(t => t.id === activeTab);
                      if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1].id);
                    }}
                    className="bg-slate-600 hover:bg-slate-500 text-white"
                  >
                    Sonraki →
                  </Button>
                ) : null}
                <Button
                  onClick={handleSubmit}
                  disabled={actionLoading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlacesPage;
